import { Box, SelectOption } from '@factorialco/deprecated-design-system'
import {
  FilterType,
  buildGetTasksDataTableFilter,
  useGetTasksDataTable,
} from 'generated/resources/datatable.graphql'
import { AccessForSelectFragment } from 'generated/resources/graphql'

import { AccessSelector } from 'modules/core/components/AccessSelector'
import useFilter from 'modules/core/components/Filter/base/useFilter'
import i18n from 'modules/core/lib/i18n'

import { taskStatuses } from 'modules/tasks/lib/status'

const statusOptions = (): SelectOption[] => {
  return [
    {
      label: i18n.t('tasks.filter.all_tasks'),
      value: 'all',
    },
    {
      label: i18n.t('tasks.filter.created_tasks'),
      value: 'owned',
    },
    {
      label: i18n.t('tasks.filter.assigned_incompleted'),
      value: 'incompleted',
    },
    {
      label: i18n.t('tasks.filter.assigned_completed'),
      value: 'completed',
    },
  ]
}

const taskStatusOptions = (): SelectOption[] => {
  return taskStatuses().map((status) => ({
    label: status.title,
    value: status.status,
  }))
}

const statusFilter = (source: ReturnType<typeof useGetTasksDataTable>) =>
  buildGetTasksDataTableFilter(source, {
    type: FilterType.String,
    categories: [
      {
        kind: 'SingleChoice',
        operation: 'eq',
        options: taskStatusOptions(),
      },
    ],
    identifier: 'tasksList.statusFilter',
    path: 'status',
    title: i18n.t('tasks.filter.status'),
  })

const categoryFilter = (source: ReturnType<typeof useGetTasksDataTable>) =>
  buildGetTasksDataTableFilter(source, {
    type: FilterType.String,
    categories: [
      {
        kind: 'SingleChoice',
        operation: 'eq',
        options: statusOptions(),
      },
    ],
    identifier: 'tasksList.categoryFilter',
    path: 'category',
    title: i18n.t('tasks.filter.category'),
  })

const accessSelectorFilter = (source: ReturnType<typeof useGetTasksDataTable>) =>
  buildGetTasksDataTableFilter(source, {
    type: FilterType.Entity,
    categories: [
      {
        kind: 'Entity',
        operation: 'in',
        nameAttribute: 'fullName',
        component: () => <AccessFilterBody />,
      },
    ],
    identifier: 'processesTaskList.accessSelectorFilter',
    path: 'assigneeIds',
    title: i18n.t('tasks.filter.assignee'),
  })

const dueDateFilter = (source: ReturnType<typeof useGetTasksDataTable>) =>
  buildGetTasksDataTableFilter(source, {
    identifier: 'tasksList.dueDateFilter',
    title: i18n.t('tasks.filter.due_date'),
    path: 'dueOn',
    default: false,
    type: FilterType.Date,
    categories: [
      {
        kind: 'DateTimePicker',
        operation: 'between',
        value: undefined,
      },
    ],
  })

const identifierFilter = (source: ReturnType<typeof useGetTasksDataTable>) =>
  buildGetTasksDataTableFilter(source, {
    identifier: 'tasksList.identifierFilter',
    title: i18n.t('tasks.filter.source.title'),
    path: 'identifier',
    type: FilterType.String,
    categories: [
      {
        kind: 'SingleChoice',
        operation: 'eq',
        options: [
          {
            label: i18n.t('tasks.filter.source.options.project'),
            value: 'project',
          },
          {
            label: i18n.t('tasks.filter.source.options.workflow'),
            value: 'workflow',
          },
        ],
      },
    ],
  })

export const filters = (source: ReturnType<typeof useGetTasksDataTable>) => [
  statusFilter(source),
  categoryFilter(source),
  accessSelectorFilter(source),
  dueDateFilter(source),
  identifierFilter(source),
]

const AccessFilterBody = () => {
  const { value, onValueChange } = useFilter<AccessForSelectFragment[]>()

  return (
    <Box width="s400" flexDirection="column" overflowY="hidden" overflowX="hidden" hideScrollBar>
      <AccessSelector
        hideBorder
        alwaysOpen
        withAvatar
        width="s400"
        multiple
        value={value}
        onSelect={onValueChange}
      />
    </Box>
  )
}
